import type {
  CarrierInitializationDiscoveryAccount,
  CarrierInitializationDiscoveryReview,
  CarrierInitializationDiscoveryVendor,
} from '@/services/api';
import { getCarrierDiscoveryReviewPresentation } from './carrierDiscoveryReviewState';

function CustomerRecord({ account }: { account: CarrierInitializationDiscoveryAccount }) {
  return (
    <li className="rounded-lg border border-surface-200 bg-white px-3 py-2.5 dark:border-surface-700 dark:bg-surface-900">
      <p className="truncate text-xs font-semibold text-surface-900 dark:text-surface-100">
        {account.name}
      </p>
      {account.company.name !== account.name && (
        <p className="mt-0.5 truncate text-[0.7rem] text-surface-500 dark:text-surface-400">
          {account.company.name}
        </p>
      )}
    </li>
  );
}

function VendorRecord({ vendor }: { vendor: CarrierInitializationDiscoveryVendor }) {
  return (
    <li className="rounded-lg border border-surface-200 bg-white px-3 py-2.5 dark:border-surface-700 dark:bg-surface-900">
      <p className="truncate text-xs font-semibold text-surface-900 dark:text-surface-100">
        {vendor.name}
      </p>
    </li>
  );
}

export function CarrierDiscoveryReview({
  review,
}: {
  review: CarrierInitializationDiscoveryReview;
}) {
  const presentation = getCarrierDiscoveryReviewPresentation({
    accountCount: review.accounts.length,
    vendorCount: review.vendors.length,
    sourceComplete: review.sourceComplete,
    sourceTruncated: review.sourceTruncated,
    hasMoreAccounts: review.hasMoreAccounts,
    hasMoreVendors: review.hasMoreVendors,
  });

  return (
    <div className="mt-3">
      <div>
        <p className="text-sm font-semibold text-surface-900 dark:text-surface-100">
          {presentation.summary}
        </p>
        {presentation.notice && (
          <p className="mt-1 text-xs leading-5 text-amber-700 dark:text-amber-300">
            {presentation.notice}
          </p>
        )}
      </div>
      <div className={`mt-2 grid gap-2 ${review.accounts.length > 0 && review.vendors.length > 0 ? 'sm:grid-cols-2' : ''}`}>
        {review.accounts.length > 0 && (
          <section aria-label="Found accounts">
            <h4 className="mb-1.5 text-[0.7rem] font-semibold uppercase tracking-wide text-surface-500 dark:text-surface-400">
              Accounts
            </h4>
            <ul className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
              {review.accounts.map((account) => (
                <CustomerRecord key={account.providerAccountId} account={account} />
              ))}
            </ul>
          </section>
        )}
        {review.vendors.length > 0 && (
          <section aria-label="Found carriers">
            <h4 className="mb-1.5 text-[0.7rem] font-semibold uppercase tracking-wide text-surface-500 dark:text-surface-400">
              Carriers
            </h4>
            <ul className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
              {review.vendors.map((vendor) => (
                <VendorRecord key={vendor.providerCarrierId} vendor={vendor} />
              ))}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
